/**
 * Validation helper for the photo_upload question type.
 *
 * Used by the SurveySession to decide whether the respondent can advance to
 * the next question. The respondent is allowed to proceed when:
 *   - At least one image has finished UPLOADING (`extraction_status` is
 *     'completed', 'processing' or 'failed' — the image is already in S3; the
 *     product extraction that follows is researcher-side enrichment, not a gate).
 *     Previously this required `=== 'completed'`, so a valid photo whose extraction
 *     returned zero products (status 'failed') left "Next" disabled. OR
 *   - The respondent provided a non-empty typed answer via the
 *     ``text_fallback`` field (the fail-safe still offered when parsing fails).
 */

export interface PhotoUploadResponseLike {
  images?: Array<{ extraction_status?: string }>;
  text_fallback?: string;
  [key: string]: unknown;
}

// extraction_status values that mean the image reached S3 (vs. an in-flight
// 'uploading'/'pending' placeholder). Product extraction (completed vs failed) is
// researcher-side enrichment, not a gate on whether the respondent has answered.
const UPLOADED_STATUSES = new Set(['completed', 'processing', 'failed']);

export const isPhotoUploadResponseValid = (response: PhotoUploadResponseLike | null | undefined): boolean => {
  if (!response) return false;

  const hasUploadedImage =
    Array.isArray(response.images) &&
    response.images.some((img) => UPLOADED_STATUSES.has(img?.extraction_status ?? ''));

  const hasTextFallback =
    typeof response.text_fallback === 'string' &&
    response.text_fallback.trim().length > 0;

  return hasUploadedImage || hasTextFallback;
};
